/**
 * /app: where the app opens (the Navigation review, D2 and D11, approved by Arjun on 2026-10-06), in this order:
 *   - after a sign-in, the page the person had open (lib/nav/landing.ts: rememberReturnTo, from the sign-in page);
 *   - after an org switch, the menu page they had open, with its tab, where the new org's menu shows it;
 *   - else the landing: Today where a brief is served, Dashboards where none is (or its read fails or takes longer
 *     than LANDING_WAIT_MS), Fleet for a vendor's admins and Settings for its other members.
 * It renders inside AppLayout, so the org, its packs and the menu's reads are the layout's own.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAppMenu } from "@/lib/nav/use-app-menu";
import { LANDING_WAIT_MS, keptPageShown, landingOf } from "@/lib/nav/landing";
import { clearKeptPage, clearReturnTo, peekKeptPage, peekReturnTo } from "@/lib/nav/return-to";

export function AppLanding() {
  const menu = useAppMenu();
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setTimedOut(true), LANDING_WAIT_MS);
    return () => window.clearTimeout(t);
  }, []);
  // Read once, when /app opens, and cleared at once, so neither outlives this landing: a click away while it waits
  // leaves no page behind for a later /app (#1211 review, LOW 2).
  const [returnTo] = useState(() => peekReturnTo());
  const [kept] = useState(peekKeptPage);
  useEffect(() => {
    clearReturnTo();
    clearKeptPage();
  }, []);

  let target: string | null;
  if (returnTo) {
    target = returnTo;
  } else {
    const landing = landingOf({ ...menu.landing, timedOut });
    if (kept) {
      // The kept page waits for the reads that decide whether the new org shows it, or for the landing's wait.
      const ready = !menu.pending || timedOut;
      target = ready ? (keptPageShown(menu.areas, kept, menu.context) ? kept : landing) : null;
    } else {
      target = landing;
    }
  }

  if (!target) {
    return (
      <div role="status" className="flex h-full items-center justify-center gap-2 p-8 text-sm text-on-surface-muted">
        <Loader2 aria-hidden className="size-4 animate-spin" />
        Opening your plant
      </div>
    );
  }
  return <Navigate to={target} replace />;
}
